/**
 * SSD Intranet API Types
 * Matched to actual Apps Script Code.gs API responses
 * Based on analysis completed 2024-11-28
 */

// =============================================================================
// API RESPONSE TYPES (match actual Apps Script output)
// =============================================================================

/**
 * Raw directory entry as returned from Apps Script API
 * Matches processDirectoryData() output in Code.gs
 */
export interface ApiDirectoryEntry {
  ssId: string;         // Spreadsheet ID
  tabName: string;      // Tab name
  name: string;         // Display name (from "Name in Index")
  url: string;          // Direct link
  link: string;         // Same as url (backward compat)
  categories: string[]; // Category tags array
  cats: string[];       // Legacy alias for categories
  desc: string;         // Description (from "Sheet Name")
  team: string;         // Team assignment (may be CSV)
  access: string;       // Email list (CSV string, NOT array)
}

/**
 * Facets response from getFacetsForViewer()
 */
export interface ApiFacets {
  total: number;
  teams: Array<{ name: string; count: number }>;
  categories: Array<{ name: string; count: number }>;
}

/**
 * Branding response from getBranding()
 */
export interface ApiBranding {
  title: string;
  logoDataUrl: string;  // Base64 data URL for logo image
}

/**
 * User info from getCurrentUserEmail()
 */
export interface UserPreferences {
  favorites: string[]; // List of bookmarked URLs
  theme?: 'dark' | 'light'; // User's theme preference
}

export interface ApiUser {
  email: string;
  authenticated: boolean;
}

/**
 * Health check response from getSystemHealth()
 */
export interface ApiHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  cacheStatus: boolean;
  spreadsheetConnected: boolean;
}

// =============================================================================
// API RESPONSE WRAPPERS (actual API response format)
// =============================================================================

/**
 * Standard API response wrapper from Apps Script
 */
export interface ApiResponse<T> {
  success: boolean;
  authenticated?: boolean;
  user?: string;
  count?: number;
  data?: T;
  timestamp: string;
  error?: string;
  // Added by client-side caching
  cached?: boolean;
  cacheAge?: number;
}

/**
 * Directory endpoint response
 * GET ?api=true&endpoint=directory
 */
export interface DirectoryApiResponse {
  success: boolean;
  authenticated: boolean;
  user: string;
  count: number;
  data: ApiDirectoryEntry[];
  timestamp: string;
}

/**
 * Facets endpoint response
 * GET ?api=true&endpoint=facets
 */
export interface FacetsApiResponse {
  success: boolean;
  total: number;
  teams: Array<{ name: string; count: number }>;
  categories: Array<{ name: string; count: number }>;
  timestamp: string;
}

/**
 * Branding endpoint response
 * GET ?api=true&endpoint=branding
 */
export interface BrandingApiResponse {
  success: boolean;
  branding: ApiBranding;
  timestamp: string;
}

/**
 * User endpoint response
 * GET ?api=true&endpoint=user
 */
export interface UserApiResponse {
  success: boolean;
  authenticated: boolean;
  user: string;
  timestamp: string;
}

/**
 * Health endpoint response
 * GET ?api=true&endpoint=health
 */
export interface HealthApiResponse {
  success: boolean;
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  cacheStatus: boolean;
  spreadsheetConnected: boolean;
}

// =============================================================================
// NORMALIZED UI TYPES (for React components)
// =============================================================================

/**
 * Normalized directory entry for UI consumption
 * Transformed from ApiDirectoryEntry
 */
export interface DirectoryEntry {
  id: string;           // ssId (unique identifier)
  ssId?: string;        // Parent Spreadsheet ID
  tabName?: string;     // Worksheet / Tab Name
  name: string;         // Display name
  description: string;  // desc field
  url: string;          // Link URL
  team: string;         // Team name
  categories: string[]; // Category array
  accessEmails: string[]; // Parsed from CSV access string
  customDisplayName?: string; // Optional custom display title assigned by admin
  activeTabs?: Array<{ name: string; url: string }>;
}

/**
 * Normalized facets for UI consumption
 */
export interface Facets {
  total: number;
  teams: Array<{ name: string; count: number }>;
  categories: Array<{ name: string; count: number }>;
}

/**
 * Normalized branding for UI consumption
 */
export interface Branding {
  title: string;
  logoUrl: string;  // logoDataUrl renamed for clarity
}

/**
 * Normalized user for UI consumption
 */
export interface User {
  email: string;
  isAuthenticated: boolean;
}

// =============================================================================
// UI STATE TYPES
// =============================================================================

export interface FilterState {
  teams: string[];
  categories: string[];
  search: string;
}

export interface ViewMode {
  type: 'grid' | 'list';
  darkMode: boolean;
}

export interface AppState {
  entries: DirectoryEntry[];
  facets: Facets | null;
  branding: Branding | null;
  user: User | null;
  filters: FilterState;
  viewMode: ViewMode;
  loading: boolean;
  error: string | null;
}

// =============================================================================
// API ENDPOINTS
// =============================================================================

/**
 * Apps Script API Endpoints
 * Base URL: https://script.google.com/macros/s/{DEPLOYMENT_ID}/exec
 *
 * Usage: ?api=true&endpoint={endpoint}
 */
export type ApiEndpoint = 'directory' | 'facets' | 'branding' | 'user' | 'health' | 'sms-optin' | 'bookmarks' | 'preferences';

// =============================================================================
// SMS OPT-IN TYPES
// =============================================================================

/**
 * SMS opt-in request payload
 */
export interface SmsOptInRequest {
  phoneNumber: string;
  isOptedIn: boolean;
  email: string;
}

/**
 * SMS opt-in response from API
 */
export interface SmsOptInResponse {
  success: boolean;
  message: string;
  error?: string;
  timestamp: string;
}

/**
 * SMS opt-in status check response
 */
export interface SmsOptInStatusResponse {
  success: boolean;
  isOptedIn: boolean;
  phoneNumber?: string;
  optedInAt?: string;
  error?: string;
}

// =============================================================================
// TRANSFORMATION UTILITIES
// =============================================================================

/**
 * Transform raw API entry to normalized UI entry
 */
export function transformDirectoryEntry(api: ApiDirectoryEntry, index: number): DirectoryEntry {
  // Create unique ID from ssId + tabName
  // Only use index if tabName is missing, to prevent ID shifting bugs when list order changes
  const uniqueId = (api.ssId && api.tabName)
    ? `${api.ssId}-${api.tabName}`
    : `${api.ssId || 'unknown'}-${api.tabName || 'tab'}-${index}`;

  return {
    id: uniqueId,
    ssId: api.ssId || '',
    tabName: api.tabName || api.name || '',
    name: api.name,
    description: api.desc || '',
    url: api.url || api.link,
    team: api.team || '',
    categories: api.categories || api.cats || [],
    accessEmails: api.access ? api.access.split(',').map(e => e.trim().toLowerCase()) : [],
  };
}

/**
 * Transform raw API branding to normalized branding
 */
export function transformBranding(api: ApiBranding): Branding {
  return {
    title: api.title,
    logoUrl: api.logoDataUrl,
  };
}

/**
 * Transform raw API user to normalized user
 */
export function transformUser(email: string, authenticated: boolean): User {
  return {
    email,
    isAuthenticated: authenticated,
  };
}
